import { apiClient } from './api';

export type OpsSeverity = 'INFO' | 'WARNING' | 'CRITICAL' | 'SECURITY';

export type HostMetrics = {
  hostname: string;
  os: string;
  uptimeSec: number;
  cpuPercent: number;
  load1: number;
  load5: number;
  load15: number;
  memTotalMb: number;
  memUsedMb: number;
  memAvailMb: number;
  memPercent: number;
  swapTotalMb: number;
  swapUsedMb: number;
  swapPercent: number;
  diskTotalGb: number;
  diskUsedGb: number;
  diskAvailGb: number;
  diskPercent: number;
  inodePercent: number;
  collectedAt: string;
};

export type ContainerMetrics = {
  name: string;
  image: string;
  status: string;
  health: string;
  running: boolean;
  restartCount: number;
  startedAt?: string;
  cpuPercent: number;
  memUsedMb: number;
  memLimitMb: number;
  available: boolean;
  error?: string;
};

export type StorageItem = {
  id: string;
  label: string;
  path?: string;
  bytes: number;
  available: boolean;
  unavailableReason?: string;
};

export type BackupStatus = {
  available: boolean;
  unavailableReason?: string;
  dir?: string;
  lastSuccessAt?: string;
  lastSuccessAgeSec?: number;
  lastSuccessSizeMb?: number;
  dirUsageMb?: number;
  timerActive?: boolean | null;
  serviceLastResult?: string;
  retentionHint?: string;
};

export type OpsAlert = {
  id: string;
  key: string;
  severity: OpsSeverity;
  title: string;
  message: string;
  host: string;
  service?: string;
  metric?: string;
  value?: string;
  threshold?: string;
  action?: string;
  adminPath?: string;
  createdAt: string;
  recoveredAt?: string;
  acknowledged: boolean;
  active: boolean;
  category: string;
};

export type OpsOverview = {
  agentOk: boolean;
  host: HostMetrics;
  containers: ContainerMetrics[];
  storage: StorageItem[];
  backup: BackupStatus;
  activeAlerts: OpsAlert[];
  collectedAt: string;
  monitoredContainers: string[];
};

export type CleanupPreview = {
  id: string;
  label: string;
  description: string;
  commandSummary: string;
  estimatedBytes: number;
  available: boolean;
  reason?: string;
};

export type CleanupResult = {
  id: string;
  ok: boolean;
  estimatedBytes: number;
  reclaimedBytes: number;
  diskBeforePct: number;
  diskAfterPct: number;
  durationMs: number;
  outputSummary: string;
  error?: string;
  initiatedBy?: string;
};

export type SchedulerConfig = {
  enabled: boolean;
  buildCacheEnabled: boolean;
  minCacheAgeHours: number;
  diskThresholdPct: number;
  maxRunsPerDay: number;
  minFreeDiskGb: number;
  maxDurationSec: number;
  lastRunAt?: string;
  nextRunAt?: string;
  lastResult?: string;
};

export type SecurityEvent = {
  id: string;
  severity: OpsSeverity;
  title: string;
  message: string;
  sourceIp?: string;
  evidence?: string;
  rule: string;
  createdAt: string;
};

export type NotificationSettings = {
  mattermostEnabled: boolean;
  channel: string;
  notifyInfo: boolean;
  notifyWarning: boolean;
  notifyCritical: boolean;
  notifySecurity: boolean;
  notifyCleanup: boolean;
  updatedAt?: string;
  webhookConfigured?: boolean;
  note?: string;
};

function unwrap<T>(res: { data?: T } | T): T {
  if (res && typeof res === 'object' && 'data' in (res as object) && (res as { data?: T }).data !== undefined) {
    return (res as { data: T }).data;
  }
  return res as T;
}

export const opsService = {
  async overview() {
    const res = await apiClient.get<OpsOverview>('/admin/ops/overview');
    return unwrap(res);
  },
  async refresh() {
    const res = await apiClient.post<OpsOverview>('/admin/ops/refresh');
    return unwrap(res);
  },
  async containers() {
    const res = await apiClient.get<{ containers: ContainerMetrics[]; monitored: string[] }>('/admin/ops/containers');
    return unwrap(res);
  },
  async storage() {
    const res = await apiClient.get<StorageItem[]>('/admin/ops/storage');
    return unwrap(res);
  },
  async backup() {
    const res = await apiClient.get<BackupStatus>('/admin/ops/backup');
    return unwrap(res);
  },
  async alerts() {
    const res = await apiClient.get<{ active: OpsAlert[]; history: OpsAlert[] }>('/admin/ops/alerts');
    return unwrap(res);
  },
  async ackAlert(key: string) {
    const res = await apiClient.post<{ ok: boolean }>('/admin/ops/alerts/ack', { key });
    return unwrap(res);
  },
  async cleanupPreview() {
    const res = await apiClient.get<CleanupPreview[]>('/admin/ops/cleanup/preview');
    return unwrap(res);
  },
  async cleanupRun(id: string) {
    const res = await apiClient.post<CleanupResult>('/admin/ops/cleanup/run', { id, confirm: id });
    return unwrap(res);
  },
  async cleanupHistory() {
    const res = await apiClient.get<CleanupResult[]>('/admin/ops/cleanup/history');
    return unwrap(res);
  },
  async getScheduler() {
    const res = await apiClient.get<SchedulerConfig>('/admin/ops/scheduler');
    return unwrap(res);
  },
  async putScheduler(cfg: SchedulerConfig) {
    const res = await apiClient.put<SchedulerConfig>('/admin/ops/scheduler', cfg);
    return unwrap(res);
  },
  async securityEvents() {
    const res = await apiClient.get<SecurityEvent[]>('/admin/ops/security/events');
    return unwrap(res);
  },
  async securityStatus() {
    const res = await apiClient.get<Record<string, string>>('/admin/ops/security/status');
    return unwrap(res);
  },
  async getNotifications() {
    const res = await apiClient.get<NotificationSettings>('/admin/ops/notifications');
    return unwrap(res);
  },
  async putNotifications(s: NotificationSettings) {
    const res = await apiClient.put<NotificationSettings>('/admin/ops/notifications', s);
    return unwrap(res);
  },
};

export function formatBytes(n: number): string {
  if (!n || n < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatUptime(sec: number): string {
  if (!sec) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}د ${h}س`;
  if (h > 0) return `${h}س ${m}د`;
  return `${m}د`;
}
